import React, { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import P from '../../components/texts/p/P';
import H from '../../components/texts/h/H';
import { useClientTheme } from '../../context/ThemeContext';
import {
  CURRENCY_CODES,
  APPLICATION_STATUS,
  MONTH_NAMES,
} from '../../utils/Constants';
import { isCommitteeStatus } from '../../utils/applicationStatusUtils';
import DropDown from '../../components/dropdown/DropDown';
import { useFormik } from 'formik';
import { formatCurrency } from '../../utils/Masks';
import {
  saveBalanceItems,
  updateBalanceItems,
} from '../../services/RiskService';
import { Toast, useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';
import Divider from '../../components/divider/Divider';
import Span from '../../components/texts/span/Span';
import Grid from '../../components/Grid/Grid';
import { balanceInfo } from '../../types/interfaces';
import Modal from '../../components/modal/modal';
import TextArea from '../../components/textarea/TextArea';
import { updateApplication } from '../../services/ApplicationService';
import { useLocation, useNavigate } from 'react-router-dom';
import useRole from '../../hooks/useRole';
import {
  hasData,
  hasError,
  isEmptyValue,
  isSuccessfulResponse,
  toNumber,
  computeGrossResultTotal,
  computeBalanceSheetPayloadTotals,
  computeResultForTheYear,
} from '../../utils/Utils';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../utils/listNavigationState';
import { NumericFormat } from 'react-number-format';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

interface BalanceActivityProps {
  cuit: string | undefined;
  applicationId: string | undefined;
  balanceInfo: balanceInfo | undefined;
  handleNextStep: Function;
  handlePurchasesSalesData: Function;
  setBalanceInfo: Function;
  setLocalBalanceId: Function;
  localBalanceId: Number;
  applicationStatus: number | string;
  categoryType?: { id: number; name: string } | null;
  onBalanceSaved?: (code: string) => void;
}

const BalanceLoader: React.FC<BalanceActivityProps> = ({
  cuit,
  applicationId,
  balanceInfo,
  handleNextStep,
  handlePurchasesSalesData,
  setBalanceInfo,
  setLocalBalanceId,
  localBalanceId,
  applicationStatus,
  categoryType,
  onBalanceSaved,
}) => {
  const { clientTheme } = useClientTheme();
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const localCurrencyCode = resolveLocalCurrencyCode(config);
  const isUruguayEnabled = config?.isUruguayEnabled === true;
  const isResultadoBrutoFiltered = config?.isResultadoBrutoFilteredEnabled ?? false;
  const isComplementaryDataEnabled = config?.isComplementaryDataEnabled ?? true;
  const isDetailedNetWorth = config?.isDetailedNetWorthEnabled ?? false;
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const { canTransitionRisk } = useRole();
  const [totals, setTotals] = useState({
    current_assets: '0,00',
    non_current_assets: '0,00',
    current_liabilities: '0,00',
    non_current_liabilities: '0,00',
    non_controlling_interest_result: '0,00',
    total_net_worth_and_non_current: '0,00',
    total_current: '0,00',
    total_non_current: '0,00',
    total_net_worth: '0,00',
    net_worth: '0,00',
    gross_result: '0,00',
    gross_result_numeric: 0,
    operating_result: '0,00',
    ordinary_result: '0,00',
    result_before_taxes: {
      extraordinary_income: '0,00',
      extraordinary_expenses: '0,00',
      total: 0
    },
    result_before_taxes_calculated: 0,
    taxes: '0,00',
  });

  // Función para normalizar net_worth cuando viene del backend
  const normalizeNetWorth = (netWorth: any) => {
    if (!netWorth) {
      return {
        capital: '',
        legal_reserves: '',
        other_reserves: '',
        capital_adjustment: '',
        irrevocable_contributions: '',
        technical_revaluation: '',
        prior_accumulated: '',
        result_of_period: '',
        other: '',
      };
    }
    
    // Función helper para convertir valores a string, preservando 0 y números
    const toValueString = (value: any): string => {
      if (value === null || value === undefined || value === '') return '';
      // Si es número (incluyendo 0), convertirlo a string
      if (typeof value === 'number') {
        // Preservar 0 como '0', otros números como string
        return String(value);
      }
      // Si ya es string, retornarlo tal cual
      if (typeof value === 'string') {
        return value;
      }
      return '';
    };
    
    if (!isDetailedNetWorth) {
      const toNum = (v: any): number => {
        if (v === null || v === undefined || v === '') return 0;
        if (typeof v === 'number') return v;
        const n = parseFloat(String(v));
        return isNaN(n) ? 0 : n;
      };

      const capitalCalc = toNum(netWorth.capital) + toNum(netWorth.capital_adjustment);
      const otherCalc = toNum(netWorth.legal_reserves) + toNum(netWorth.other_reserves) +
        toNum(netWorth.irrevocable_contributions) + toNum(netWorth.technical_revaluation) +
        toNum(netWorth.prior_accumulated) + toNum(netWorth.result_of_period) + toNum(netWorth.other);

      return {
        capital: String(Math.round(capitalCalc)),
        legal_reserves: '',
        other_reserves: '',
        capital_adjustment: '',
        irrevocable_contributions: '',
        technical_revaluation: '',
        prior_accumulated: '',
        result_of_period: '',
        other: String(Math.round(otherCalc)),
      };
    }

    const normalized = {
      capital: toValueString(netWorth.capital),
      legal_reserves: toValueString(netWorth.legal_reserves),
      other_reserves: toValueString(netWorth.other_reserves),
      capital_adjustment: toValueString(netWorth.capital_adjustment),
      irrevocable_contributions: toValueString(netWorth.irrevocable_contributions),
      technical_revaluation: toValueString(netWorth.technical_revaluation),
      prior_accumulated: toValueString(netWorth.prior_accumulated),
      result_of_period: toValueString(netWorth.result_of_period),
      other: toValueString(netWorth.other),
    };

    return normalized;
  };

  const complementaryDefaults = isComplementaryDataEnabled
    ? { purchases: 0, amortization_depreciation: 0 }
    : { purchases: '', amortization_depreciation: '' };

  const applyComplementaryDataDefaults = (data: balanceInfo): balanceInfo => {
    if (!isComplementaryDataEnabled) return data;

    return {
      ...data,
      purchases: isEmptyValue(data.purchases) ? 0 : data.purchases,
      amortization_depreciation: isEmptyValue(data.amortization_depreciation) ? 0 : data.amortization_depreciation,
    };
  };

  const [balanceData, setBalanceData] = useState<balanceInfo>(
    balanceInfo ? applyComplementaryDataDefaults({
      ...balanceInfo,
      net_worth: normalizeNetWorth(balanceInfo.net_worth),
    }) : {
      id: 0,
      from_month: 0,
      from_year: 0,
      to_month: 0,
      to_year: 0,
      current_assets: {
        current_investments: '',
        cash_and_cash_equivalents: '',
        trade_receivables_short_term: '',
        other_receivables: '',
        inventory_short_term: '',
        other_current_assets: '',
      },
      non_current_assets: {
        trade_receivables_long_term: '',
        other_receivables: '',
        permanent_investments: '',
        property_plant_and_equipment: '',
        other_non_current_assets: '',
      },
      current_liabilities: {
        trade_payables_short_term: '',
        bank_and_financial_debt_short_term: '',
        social_security_debt_short_term: '',
        tax_liabilities_short_term: '',
        other_debts_provisions: '',
        projections: '',
        other_liabilities: '',
      },
      non_current_liabilities: {
        non_current_liabilities: '',
        trade_payables_long_term: '',
        bank_and_financial_debt_long_term: '',
        social_security_debt_long_term: '',
        tax_liabilities_long_term: '',
        other_long_term_liabilities: '',
      },
      non_controlling_interest_result: {
        non_controlling_interest_result: '',
      },
      net_worth: {
        capital: '',
        legal_reserves: '',
        other_reserves: '',
        capital_adjustment: '',
        irrevocable_contributions: '',
        technical_revaluation: '',
        prior_accumulated: '',
        result_of_period: '',
        other: '',
      },
      gross_result: {
        agricultural_production: '',
        production_cost: '',
        sales: '',
        cost_of_sales: '',
      },
      operating_result: {
        commercialization_expenses: '',
        administrative_expenses: '',
        other_income: '',
        other_expenses: '',
      },
      ordinary_result: {
        financial_expenses: '',
        exchange_difference_and_holding_result: '',
      },
      result_before_taxes: {
        extraordinary_income: '',
        extraordinary_expenses: '',
      },
      taxes: {
        income_tax: '',
      },
      ...complementaryDefaults,
    }
  );

  const [keys, setKeys] = useState<string[]>([]);
  const [values, setValues] = useState<string[]>([]);

  const [selectedKey, setSelectedKey] = useState<string | null>(
    balanceData.from_month > 0 && balanceData.from_year > 0
      ? `${MONTH_NAMES[balanceData.from_month - 1]}-${balanceData.from_year}`
      : null
  );
  const [correspondingValue, setCorrespondingValue] = useState<string | null>(
    balanceData.to_month > 0 && balanceData.to_year > 0
      ? `${MONTH_NAMES[balanceData.to_month - 1]}-${balanceData.to_year}`
      : null
  );

  // Evita que el effect de período resetee los datos del form en el mount inicial
  // cuando el período se inicializa desde un balance ya cargado (ej. al volver al paso 2).
  const isPeriodInitialMountRef = React.useRef(true);

  const map = new Map();

  const xxsArea = ['input1-2 input2-2', 'input3-2 input4-2', 'input5'];
  const lArea = ['input1-4 input2-4 input3-4', 'input4-6 input5-6'];

  const xxsAreaActivo = [
    'input1-2 input2-2',
    'input3-2 input4-2',
    'input5-2 input6-2',
  ];
  const lAreaActivo = [
    'input1-4 input2-4 input3-4',
    'input4-4 input5-4 input6-4',
  ];

  const xxsAreaPasivo = [
    'input1-2 input2-2',
    'input3-2 input4-2',
    'input5-2 input6-2',
    'input7',
  ];
  const lAreaPasivo = [
    'input1-4 input2-4 input3-4',
    'input4-4 input5-4 input6-4',
    'input7',
  ];

  const isApplicationEditable = () => {
    return !isCommitteeStatus(applicationStatus);
  };

  const [localNetWorth, setlocalNetWorth] = useState<string>('');

  const balanceFormik = useFormik({
    initialValues: balanceData,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  // Actualizar cuando balanceInfo cambie
  useEffect(() => {
    if (balanceInfo) {
      console.log('[BalanceLoader] balanceInfo recibido:', balanceInfo);
      console.log('[BalanceLoader] net_worth antes de normalizar:', balanceInfo.net_worth);
      
      const normalizedNetWorth = normalizeNetWorth(balanceInfo.net_worth);
      console.log('[BalanceLoader] net_worth después de normalizar:', normalizedNetWorth);
      
      const updatedData = {
        ...balanceInfo,
        net_worth: normalizedNetWorth,
      };
      
      console.log('[BalanceLoader] balanceData actualizado:', updatedData);
      
      const balanceDataWithDefaults = applyComplementaryDataDefaults(updatedData);
      setBalanceData(balanceDataWithDefaults);
      balanceFormik.setValues(balanceDataWithDefaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceInfo]);

  useEffect(() => {
    const sumValues = (obj) =>
      Object.values(obj).reduce((sum, val) => sum + toNumber(val), 0);

    const formatEuro = (value: number) => {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }).format(value);
    };
    
    // El cálculo del Resultado Bruto siempre usa la fórmula completa, independientemente del flag
    // El flag solo afecta la visualización (ocultar campos)
    const gross_result = Math.round((
      toNumber(balanceFormik.values.gross_result.agricultural_production) -
      toNumber(balanceFormik.values.gross_result.production_cost) +
      toNumber(balanceFormik.values.gross_result.sales) -
      toNumber(balanceFormik.values.gross_result.cost_of_sales)
    ) * 100) / 100;

    const operating_result = Math.round((
      gross_result -
      toNumber(
        balanceFormik.values.operating_result.commercialization_expenses
      ) -
      toNumber(balanceFormik.values.operating_result.administrative_expenses) +
      toNumber(balanceFormik.values.operating_result.other_income) -
      toNumber(balanceFormik.values.operating_result.other_expenses)
    ) * 100) / 100;

    const ordinary_result = Math.round((
      operating_result -
      toNumber(balanceFormik.values.ordinary_result.financial_expenses) +
      toNumber(
        balanceFormik.values.ordinary_result
          .exchange_difference_and_holding_result
      )
    ) * 100) / 100;

    const result_before_taxes = Math.round((
      ordinary_result +
      toNumber(balanceFormik.values.result_before_taxes.extraordinary_income) -
      toNumber(balanceFormik.values.result_before_taxes.extraordinary_expenses)
    ) * 100) / 100;

    const taxes = Math.round((
      result_before_taxes + toNumber(balanceFormik.values.taxes.income_tax)
    ) * 100) / 100;

    const total_current =
      toNumber(sumValues(balanceFormik.values.current_assets)) +
      toNumber(sumValues(balanceFormik.values.non_current_assets));
    const total_non_current =
      toNumber(sumValues(balanceFormik.values.current_liabilities)) +
      toNumber(sumValues(balanceFormik.values.non_current_liabilities));
    const total_net_worth = isDetailedNetWorth
      ? toNumber(sumValues(balanceFormik.values.net_worth))
      : toNumber(balanceFormik.values.net_worth.capital || 0) +
        toNumber(balanceFormik.values.net_worth.other || 0);
    const total_net_worth_and_non_current =
      toNumber(sumValues(balanceFormik.values.current_liabilities)) +
      toNumber(sumValues(balanceFormik.values.non_current_liabilities)) +
      total_net_worth +
      toNumber(sumValues(balanceFormik.values.non_controlling_interest_result));
    setlocalNetWorth(formatEuro(total_net_worth));

    setTotals({
      current_assets: formatEuro(
        sumValues(balanceFormik.values.current_assets)
      ),
      non_current_assets: formatEuro(
        sumValues(balanceFormik.values.non_current_assets)
      ),
      current_liabilities: formatEuro(
        sumValues(balanceFormik.values.current_liabilities)
      ),
      non_current_liabilities: formatEuro(
        sumValues(balanceFormik.values.non_current_liabilities)
      ),
      non_controlling_interest_result: formatEuro(
        sumValues(balanceFormik.values.non_controlling_interest_result)
      ),
      total_current: formatEuro(total_current),
      total_non_current: formatEuro(total_non_current),
      total_net_worth: formatEuro(total_net_worth),
      net_worth: formatEuro(total_net_worth),
      total_net_worth_and_non_current: formatEuro(
        total_net_worth_and_non_current
      ),
      gross_result: formatEuro(gross_result),
      gross_result_numeric: gross_result,
      operating_result: formatEuro(operating_result),
      ordinary_result: formatEuro(ordinary_result),
      result_before_taxes: {
        extraordinary_income: formatEuro(toNumber(balanceFormik.values.result_before_taxes.extraordinary_income)),
        extraordinary_expenses: formatEuro(toNumber(balanceFormik.values.result_before_taxes.extraordinary_expenses)),
        total: result_before_taxes
      },
      result_before_taxes_calculated: result_before_taxes,
      taxes: formatEuro(taxes),
    });
  }, [balanceFormik.values]);

  const replaceEmptyValues = (obj): any => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (key === 'result_before_taxes' && value && typeof value === 'object') {
          // Preservar el campo 'total' en result_before_taxes
          return [
            key,
            {
              extraordinary_income: isEmptyValue(value.extraordinary_income) ? 0 : toNumber(value.extraordinary_income),
              extraordinary_expenses: isEmptyValue(value.extraordinary_expenses) ? 0 : toNumber(value.extraordinary_expenses),
              total: value.total // Preservar el campo total tal como está (ya es un número)
            }
          ];
        }
        return [
          key,
          value && typeof value === 'object'
            ? replaceEmptyValues(value)
            : isEmptyValue(value)
              ? 0
              : toNumber(value as string),
        ];
      })
    );
  };

  const saveData = (shouldContinue: boolean) => {
    let updatedFinancialData = replaceEmptyValues(balanceFormik.values);

    // Resultado bruto total: mismo cálculo que el paso 2 para que origination y EBIT lo usen
    const grossResultTotal = computeGrossResultTotal(updatedFinancialData.gross_result);
    updatedFinancialData.gross_result = {
      ...updatedFinancialData.gross_result,
      total: grossResultTotal,
    };

    // Override result_before_taxes with the calculated value from totals
    updatedFinancialData.result_before_taxes = {
      extraordinary_income: updatedFinancialData.result_before_taxes.extraordinary_income,
      extraordinary_expenses: updatedFinancialData.result_before_taxes.extraordinary_expenses,
      total: totals.result_before_taxes_calculated
    };

    updatedFinancialData.result_for_the_year = computeResultForTheYear(
      totals.result_before_taxes_calculated,
      updatedFinancialData.taxes?.income_tax
    );

    // Totales de activos/pasivos para que origination persista total_assets_amount (BPB_024, etc.)
    const payloadTotals = computeBalanceSheetPayloadTotals(updatedFinancialData);
    updatedFinancialData.current_assets = {
      ...updatedFinancialData.current_assets,
      total: payloadTotals.currentAssetsTotal,
    };
    updatedFinancialData.non_current_assets = {
      ...updatedFinancialData.non_current_assets,
      total: payloadTotals.nonCurrentAssetsTotal,
    };
    updatedFinancialData.total_assets_amount = payloadTotals.totalAssetsAmount;
    updatedFinancialData.total_liabilities_amount = payloadTotals.totalLiabilitiesAmount;

    setIsSaving(true);
    setIsDisabled(true);

    if (isApplicationEditable()) {
      if (localBalanceId === 0) {
        saveBalanceItems(
          cuit || '',
          applicationId || '',
          updatedFinancialData
        ).then((response) => {
          if (hasData(response) && response.status === 201) {
            setLocalBalanceId(response.data.id);
            if (onBalanceSaved && response.data.code) {
              onBalanceSaved(response.data.code);
            }
            useToast.success({
              title: 'Se guardó la información correctamente.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });

            if (shouldContinue) {
              handleNextStep({
                values: updatedFinancialData,
                totals: totals,
              });
            }
          } else if (response.status === 409) {
            useToast.error({
              title: 'Ya existe un balance en el periodo solicitado.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          } else {
            useToast.error({
              title: 'Ocurrió un error al guardar la información.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          }
          setIsSaving(false);
          setIsDisabled(false);
          setBalanceInfo({ values: updatedFinancialData, totals: totals });
        });
      } else {
        updateBalanceItems(
          cuit || '',
          applicationId || '',
          localBalanceId,
          updatedFinancialData
        ).then((response) => {
          if (isSuccessfulResponse(response)) {
            useToast.success({
              title: 'Se guardó la información correctamente.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });

            if (shouldContinue) {
              handleNextStep({ values: updatedFinancialData, totals: totals });
            }
          } else if (hasError(response).isClientError) {
            useToast.error({
              title: 'Ya existe un balance en el periodo solicitado.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          } else {
            useToast.error({
              title: 'Ocurrió un error al guardar la información.',
              id: 'balance-activity-toast',
              description: '',
              closeButton: false,
              autoClose: 5000,
            });
          }
          setIsSaving(false);
          setIsDisabled(false);
          setBalanceInfo({ values: updatedFinancialData, totals: totals });
        });
      }
    } else {
      handleNextStep({ values: updatedFinancialData, totals: totals });
    }
  };

  useEffect(() => {
    const initializeComponent = () => {
      const startDate = new Date();
      startDate.setDate(1);
      startDate.setMonth(startDate.getMonth() - 12);

      const endDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        1
      );
      endDate.setDate(1);
      endDate.setMonth(endDate.getMonth() + 11);

      for (let i = 0; i <= 48; i++) {
        map.set(
          MONTH_NAMES[startDate.getMonth()] + '-' + startDate.getFullYear(),
          MONTH_NAMES[endDate.getMonth()] + '-' + endDate.getFullYear()
        );
        startDate.setMonth(startDate.getMonth() - 1);
        endDate.setMonth(endDate.getMonth() - 1);
      }
    };

    initializeComponent();
    const keys = Array.from(map.keys());
    const values = Array.from(map.values());
    setKeys(keys);
    setValues(values);
  }, []);

  useEffect(() => {
    if (selectedKey && correspondingValue) {
      const [fromMonth, fromYear] = selectedKey.split('-');
      const [toMonth, toYear] = correspondingValue.split('-');

      balanceFormik.setFieldValue(
        'from_month',
        MONTH_NAMES.indexOf(fromMonth) + 1
      );
      balanceFormik.setFieldValue('from_year', parseInt(fromYear));
      balanceFormik.setFieldValue('to_month', MONTH_NAMES.indexOf(toMonth) + 1);
      balanceFormik.setFieldValue('to_year', parseInt(toYear));

      // En el mount inicial el período ya viene del balance cargado: no resetear
      // los valores del form. Solo resetear cuando el usuario cambia el período.
      if (isPeriodInitialMountRef.current) {
        isPeriodInitialMountRef.current = false;
        return;
      }
      setBalanceData(balanceFormik.values);
    }
  }, [selectedKey, correspondingValue]);

  const handleNextDropDownItems = (e) => {
    const selectedKey = e;
    setSelectedKey(selectedKey);

    const index = keys.indexOf(selectedKey);
    if (index !== -1) {
      setCorrespondingValue(values[index]);
      const [fromMonth, fromYear] = selectedKey.split('-');
      const [toMonth, toYear] = values[index].split('-');

      balanceFormik.setFieldValue(
        'from_month',
        MONTH_NAMES.indexOf(fromMonth) + 1
      );
      balanceFormik.setFieldValue('from_year', parseInt(fromYear));
      balanceFormik.setFieldValue('to_month', MONTH_NAMES.indexOf(toMonth) + 1);
      balanceFormik.setFieldValue('to_year', parseInt(toYear));
    }
    setBalanceData(balanceFormik.values);
  };

  const renderInput = (
    label: string,
    parentFieldName: string,
    childFieldName: string
  ) => {
    return (
      <NumericFormat
        disabled={!selectedKey || !isApplicationEditable()}
        label={label}
        id={`risk-balance-input-${parentFieldName}.${childFieldName}`}
        maxLength={40}
        placeholder='Ingresar monto'
        value={balanceFormik.values[parentFieldName]?.[childFieldName] ?? ''}
        thousandSeparator='.'
        decimalSeparator=','
        onValueChange={(values) => {
          balanceFormik.setFieldValue(
            `${parentFieldName}.${childFieldName}`,
            values.value === '' ? '' : values.floatValue
          );
        }}
        customInput={Input}
        decimalScale={2}
        allowNegative={
          label === 'Impuestos a las ganancias' || 
          label === 'Capital' || 
          label === 'Revalúo Técnico' || 
          label === 'Ajuste de Capital' || 
          label === 'Acumuladas Ejercicio Anterior' || 
          label === 'Resultado del Ejercicio' ||
          label === 'Otros'
            ? true 
            : false
        }
      />
    );
  };

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSaving(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      }
      setIsSaving(false);
    });
  };

  return (
    <Card spacing={{ left: isUruguayEnabled ? '0' : '6' }}>
      <Toast id='balance-activity-toast' />
      <Toast id='application-toast' />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />
      <Card display='grid' gap='8px' margin={{ bottom: '2', top: '4' }}>
        <P type='1' variant='neutral' withMargin={false}>
          Período del balance
        </P>
      </Card>
      <Card display='flex' gap='16px' alignItems='center'>
        <div style={{ width: '260px', minWidth: '220px' }}>
          <DropDown
            id='balance-month-since-dropdown-from'
            items={keys.map((key) => ({ value: key, label: key }))}
            title={
              balanceData.from_month === 0 && balanceData.from_year === 0
                ? 'Desde'
                : `${selectedKey}`
            }
            onChange={(e) => handleNextDropDownItems(e)}
            disabled={!isApplicationEditable()}
          />
        </div>
        <div style={{ width: '260px', minWidth: '220px' }}>
          <DropDown
            id='balance-month-to-dropdown-to'
            title={
              balanceData.to_month === 0 && balanceData.to_year === 0
                ? 'Hasta'
                : `${correspondingValue}`
            }
            disabled
            onChange={() => {}}
          />
        </div>
      </Card>
      <Card display='flex' justifyContent='space-between'>
        <H type='3' variant='secondary-2' weight='bold'>
          Activo corriente
        </H>
        <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
          {localCurrencyCode}{' '}
          {totals.current_assets}
        </H>
      </Card>
      <Grid xxs={xxsAreaActivo} l={lAreaActivo}>
        <Grid.Area name='input1'>
          {renderInput(
            'Disponibilidades',
            'current_assets',
            'cash_and_cash_equivalents'
          )}
        </Grid.Area>
        <Grid.Area name='input2'>
          {renderInput(
            'Inversiones corrientes PF',
            'current_assets',
            'current_investments'
          )}
        </Grid.Area>
        <Grid.Area name='input3'>
          {renderInput(
            'Créditos por ventas CP',
            'current_assets',
            'trade_receivables_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input4'>
          {renderInput('Otros créditos', 'current_assets', 'other_receivables')}
        </Grid.Area>
        <Grid.Area name='input5'>
          {renderInput(
            'Bienes de cambio - CP',
            'current_assets',
            'inventory_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input6'>
          {renderInput(
            'Otros activos corrientes - CP',
            'current_assets',
            'other_current_assets'
          )}
        </Grid.Area>
      </Grid>
      <Card gap='16px' display='flex' justifyContent='space-between'>
        <H type='3' variant='secondary-2' weight='bold'>
          Activo no corriente
        </H>
        <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
          {localCurrencyCode}{' '}
          {totals.non_current_assets}
        </H>
      </Card>
      <Grid xxs={xxsArea} l={lArea}>
        <Grid.Area name='input1'>
          {renderInput(
            'Créditos por ventas LP',
            'non_current_assets',
            'trade_receivables_long_term'
          )}
        </Grid.Area>
        <Grid.Area name='input2'>
          {renderInput(
            'Otros créditos',
            'non_current_assets',
            'other_receivables'
          )}
        </Grid.Area>
        <Grid.Area name='input3'>
          {renderInput(
            'Inversiones permantentes',
            'non_current_assets',
            'permanent_investments'
          )}
        </Grid.Area>
        <Grid.Area name='input4'>
          {renderInput(
            'Bienes de uso',
            'non_current_assets',
            'property_plant_and_equipment'
          )}
        </Grid.Area>
        <Grid.Area name='input5'>
          {renderInput(
            'Otros activos no corrientes',
            'non_current_assets',
            'other_non_current_assets'
          )}
        </Grid.Area>
      </Grid>
      <Card
        border={true}
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'3'}
        display='flex'
        justifyContent='center'
        gap='16px'
        margin={{ top: '4' }}
      >
        <H type='4' withMargin={false} align='center'>
          Total activo:
        </H>
        <H
          type='4'
          withMargin={false}
          weight='bolder'
          variant={clientTheme ? 'primary' : 'success'}
          align='center'
        >
          {localCurrencyCode} {totals.total_current}
        </H>
      </Card>
      <Card gap='16px' display='flex' justifyContent='space-between'>
        <H type='3' variant='secondary-2' weight='bold'>
          Pasivo corriente
        </H>
        <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
          {localCurrencyCode} {totals.current_liabilities}
        </H>
      </Card>
      <Grid xxs={xxsAreaPasivo} l={lAreaPasivo}>
        <Grid.Area name='input1'>
          {renderInput(
            'Deudas comerciales CP',
            'current_liabilities',
            'trade_payables_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input2'>
          {renderInput(
            'Deuda bancaria y financiera CP',
            'current_liabilities',
            'bank_and_financial_debt_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input3'>
          {renderInput(
            'Deudas cargas sociales CP',
            'current_liabilities',
            'social_security_debt_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input4'>
          {renderInput(
            'Cargas fiscales CP',
            'current_liabilities',
            'tax_liabilities_short_term'
          )}
        </Grid.Area>
        <Grid.Area name='input5'>
          {renderInput(
            'Otras deudas - provisiones',
            'current_liabilities',
            'other_debts_provisions'
          )}
        </Grid.Area>
        <Grid.Area name='input6'>
          {renderInput('Previsiones', 'current_liabilities', 'projections')}
        </Grid.Area>
        <Grid.Area name='input7'>
          {renderInput(
            'Otros pasivos',
            'current_liabilities',
            'other_liabilities'
          )}
        </Grid.Area>
      </Grid>
      <Card display='flex' justifyContent='space-between'>
        <H type='3' variant='secondary-2' weight='bold'>
          Pasivo no corriente
        </H>
        <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
          {localCurrencyCode}{' '}
          {totals.non_current_liabilities}
        </H>
      </Card>
      <Grid xxs={xxsArea} l={lArea}>
        <Grid.Area name='input1'>
          {renderInput(
            'Deudas comerciales LP',
            'non_current_liabilities',
            'trade_payables_long_term'
          )}
        </Grid.Area>
        <Grid.Area name='input2'>
          {renderInput(
            'Deuda bancaria y financiera LP',
            'non_current_liabilities',
            'bank_and_financial_debt_long_term'
          )}
        </Grid.Area>
        <Grid.Area name='input3'>
          {renderInput(
            'Cargas sociales LP',
            'non_current_liabilities',
            'social_security_debt_long_term'
          )}
        </Grid.Area>
        <Grid.Area name='input4'>
          {renderInput(
            'Cargas fiscales LP',
            'non_current_liabilities',
            'tax_liabilities_long_term'
          )}
        </Grid.Area>
        <Grid.Area name='input5'>
          {renderInput(
            'Otros pasivos LP',
            'non_current_liabilities',
            'other_long_term_liabilities'
          )}
        </Grid.Area>
      </Grid>
      <Card
        border={true}
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'3'}
        display='flex'
        justifyContent='center'
        gap='16px'
        margin={{ top: '4' }}
      >
        <H type='4' withMargin={false} align='center'>
          Total pasivo:
        </H>
        <H
          type='4'
          withMargin={false}
          weight='bolder'
          variant={clientTheme ? 'primary' : 'success'}
          align='center'
        >
          {localCurrencyCode}{' '}
          {totals.total_non_current}
        </H>
      </Card>
      <Card display='flex' justifyContent='space-between'>
        <H type='3' variant='secondary-2' weight='bold'>
          Patrimonio neto
        </H>
        <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
          {localNetWorth}
        </H>
      </Card>
      {isDetailedNetWorth ? (
        <Card display='grid' gap='16px'>
          {renderInput('Capital', 'net_worth', 'capital')}
          {renderInput('Reservas Legales', 'net_worth', 'legal_reserves')}
          {renderInput('Otras Reservas', 'net_worth', 'other_reserves')}
          {renderInput('Ajuste de Capital', 'net_worth', 'capital_adjustment')}
          {renderInput('Aportes Irrevocables', 'net_worth', 'irrevocable_contributions')}
          {renderInput('Revalúo Técnico', 'net_worth', 'technical_revaluation')}
          {renderInput('Acumuladas Ejercicio Anterior', 'net_worth', 'prior_accumulated')}
          {renderInput('Resultado del Ejercicio', 'net_worth', 'result_of_period')}
          {renderInput('Otros', 'net_worth', 'other')}
        </Card>
      ) : (
        <Grid xxs={['input1-2 input2-2']} l={['input1-2 input2-2']}>
          <Grid.Area name='input1'>
            {renderInput('Capital', 'net_worth', 'capital')}
            <Span variant='neutral' type='2'>
              Si el monto es negativo, agregar el signo "-".
            </Span>
          </Grid.Area>
          <Grid.Area name='input2'>
            {renderInput('Otros', 'net_worth', 'other')}
            <Span variant='neutral' type='2'>
              Si el monto es negativo, agregar el signo "-".
            </Span>
          </Grid.Area>
        </Grid>
      )}
      <Card
        border={true}
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'3'}
        display='flex'
        margin={{ top: '4' }}
        gap='16px'
        justifyContent='center'
      >
        <H type='4' withMargin={false}>
          Total pasivo + Patrimonio neto:
        </H>
        <H
          type='4'
          withMargin={false}
          weight='bolder'
          variant={clientTheme ? 'primary' : 'success'}
          align='left'
        >
          {localCurrencyCode}{' '}
          {totals.total_net_worth_and_non_current}
        </H>
      </Card>
      <Card display='flex'>
        <Card width='50%' display='grid' gap='16px'>
          <H type='3' variant='secondary-2' weight='bold'>
            Resultado bruto
          </H>
          {renderInput('Ventas', 'gross_result', 'sales')}
          {renderInput('Costo de ventas', 'gross_result', 'cost_of_sales')}
          {!isResultadoBrutoFiltered && (
            <>
              {renderInput(
                'Producción agropecuaria',
                'gross_result',
                'agricultural_production'
              )}
              {renderInput(
                'Costo de producción',
                'gross_result',
                'production_cost'
              )}
            </>
          )}
        </Card>
        <Card
          width='50%'
          display='flex'
          alignItems='end'
          justifyContent='flex-end'
        >
          <Card>
            <H type='4' withMargin={false} variant={clientTheme ? 'primary' : 'success'} align='right'>
              Total resultado bruto:
            </H>
            <H
              type='4'
              withMargin={false}
              variant={
                totals.gross_result_numeric >= 0
                  ? (clientTheme ? 'primary' : 'success')
                  : 'danger'
              }
              align='right'
              weight='bolder'
            >
              {localCurrencyCode}{' '}
              {totals.gross_result}
            </H>
          </Card>
        </Card>
      </Card>
      <Divider variant='neutral' margin={{ top: '4' }} />
      <Card display='flex'>
        <Card width='50%' display='grid' gap='16px'>
          <H type='3' variant='secondary-2' weight='bold'>
            Resultado operativo
          </H>
          {renderInput(
            'Gastos de comercialización',
            'operating_result',
            'commercialization_expenses'
          )}
          {renderInput(
            'Gastos de administración',
            'operating_result',
            'administrative_expenses'
          )}
          {renderInput('Otros ingresos', 'operating_result', 'other_income')}
          {renderInput('Otros egresos', 'operating_result', 'other_expenses')}
        </Card>
        <Card
          width='50%'
          display='flex'
          alignItems='end'
          justifyContent='flex-end'
        >
          <Card>
            <H type='4' withMargin={false} variant={clientTheme ? 'primary' : 'success'} align='right'>
              Total resultado operativo:
            </H>
            <H
              type='4'
              withMargin={false}
              variant={clientTheme ? 'primary' : 'success'}
              align='right'
              weight='bolder'
            >
              {localCurrencyCode}{' '}
              {totals.operating_result}
            </H>
          </Card>
        </Card>
      </Card>
      <Divider variant='neutral' margin={{ top: '4' }} />
      <Card display='flex'>
        <Card width='50%' display='grid' gap='16px'>
          <H type='3' variant='secondary-2' weight='bold'>
            Resultado ordinario
          </H>
          {renderInput(
            'Gastos financieros',
            'ordinary_result',
            'financial_expenses'
          )}
          <NumericFormat
            disabled={!selectedKey || !isApplicationEditable()}
            label='Dif. de cambio + res. tenencia'
            id={`risk-balance-input-ordinary_result.exchange_difference_and_holding_result`}
            maxLength={40}
            placeholder='Ingresar monto'
            value={
              balanceFormik.values.ordinary_result
                .exchange_difference_and_holding_result ?? ''
            }
            thousandSeparator='.'
            decimalSeparator=','
            onValueChange={(values) =>
              balanceFormik.setFieldValue(
                'ordinary_result.exchange_difference_and_holding_result',
                values.value === '' ? '' : values.floatValue
              )
            }
            customInput={Input}
            decimalScale={2}
            allowNegative={true}
          />
          <Span variant='neutral' type='2'>
            Si el monto es negativo, agregar el signo "-".
          </Span>
        </Card>
        <Card
          width='50%'
          display='flex'
          alignItems='end'
          justifyContent='flex-end'
        >
          <Card>
            <H type='4' withMargin={false} variant={clientTheme ? 'primary' : 'success'} align='right'>
              Total resultado ordinario:
            </H>
            <H
              type='4'
              withMargin={false}
              variant={clientTheme ? 'primary' : 'success'}
              align='right'
              weight='bolder'
            >
              {localCurrencyCode}{' '}
              {totals.ordinary_result}
            </H>
          </Card>
        </Card>
      </Card>
      <Divider variant='neutral' margin={{ top: '4' }} />
      <Card display='flex'>
        <Card width='50%' display='grid' gap='16px'>
          <H type='3' variant='secondary-2' weight='bold'>
            Resultado antes de impuestos
          </H>
          {renderInput(
            'Ingresos extraordinarios',
            'result_before_taxes',
            'extraordinary_income'
          )}
          {renderInput(
            'Egresos extraordinarios',
            'result_before_taxes',
            'extraordinary_expenses'
          )}
        </Card>
        <Card
          width='50%'
          display='flex'
          alignItems='end'
          justifyContent='flex-end'
        >
          <Card>
            <H type='4' withMargin={false} variant={clientTheme ? 'primary' : 'success'} align='right'>
              Total resultado antes de impuestos:
            </H>
            <H
              type='4'
              withMargin={false}
              variant={clientTheme ? 'primary' : 'success'}
              align='right'
              weight='bolder'
            >
              {localCurrencyCode}{' '}
              {formatCurrency(totals.result_before_taxes?.total ?? 0)}
            </H>
          </Card>
        </Card>
      </Card>
      <Divider variant='neutral' margin={{ top: '4' }} />
      <Card display='flex' gap='16px'>
        <Card width='50%' display='grid' gap='16px'>
          <H type='3' variant='secondary-2' weight='bold'>
            Impuestos
          </H>
          {renderInput('Impuestos a las ganancias', 'taxes', 'income_tax')}
          <Span variant='neutral' type='2'>
            Si el monto es negativo, agregar el signo "-".
          </Span>
        </Card>
        <Card
          alignItems='center'
          border={true}
          variant={clientTheme ? 'primary' : 'success'}
          modifier='weak'
          spacing={'3'}
          display='flex'
          margin={{ top: '4' }}
          gap='16px'
          justifyContent='center'
          width='50%'
        >
          <H type='4' withMargin={false}>
            Resultado del ejercicio
          </H>
          <H
            type='4'
            withMargin={false}
            weight='bolder'
            variant={clientTheme ? 'primary' : 'success'}
            align='left'
          >
            {localCurrencyCode} {totals.taxes}
          </H>
        </Card>
      </Card>
      {isComplementaryDataEnabled && (
        <>
          <Divider variant='neutral' margin={{ top: '4' }} />
          <Card display='flex'>
            <Card width='50%' display='grid' gap='16px'>
              <H type='3' variant='secondary-2' weight='bold'>
                Datos Complementarios
              </H>
              <NumericFormat
                disabled={!selectedKey || !isApplicationEditable()}
                label='Compras'
                id='risk-balance-input-purchases'
                maxLength={40}
                placeholder='Ingresar monto'
                value={balanceFormik.values.purchases ?? ''}
                thousandSeparator='.'
                decimalSeparator=','
                onValueChange={(values) =>
                  balanceFormik.setFieldValue(
                    'purchases',
                    values.value === '' ? '' : values.floatValue
                  )
                }
                customInput={Input}
                decimalScale={2}
                allowNegative={true}
              />
              <NumericFormat
                disabled={!selectedKey || !isApplicationEditable()}
                label='Amortizaciones y Depreciaciones'
                id='risk-balance-input-amortization-depreciation'
                maxLength={40}
                placeholder='Ingresar monto'
                value={balanceFormik.values.amortization_depreciation ?? ''}
                thousandSeparator='.'
                decimalSeparator=','
                onValueChange={(values) =>
                  balanceFormik.setFieldValue(
                    'amortization_depreciation',
                    values.value === '' ? '' : values.floatValue
                  )
                }
                customInput={Input}
                decimalScale={2}
                allowNegative={true}
              />
            </Card>
          </Card>
        </>
      )}
      <Card display='flex' margin={{ top: '4' }} justifyContent='space-between'>
        {canTransitionRisk && (
          <Card width='280px'>
            <Button
              id='checkRisk-button-reject'
              size='lg'
              variant='secondary'
              onClick={() => setIsRejectApplication(true)}
              fullWidth
              disabled={isSaving}
            >
              {isSaving ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Declinar o solicitar cambios'
              )}
            </Button>
          </Card>
        )}
        <Card
          display='flex'
          justifyContent='flex-end'
          gap='16px'
          width={isApplicationEditable() ? '420px' : '210px'}
        >
          {isApplicationEditable() && (
            <Button
              id='client-button-save'
              size='lg'
              variant='primary'
              fullWidth
              onClick={() => saveData(false)}
              disabled={!selectedKey || isDisabled || isSaving}
            >
              {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
            </Button>
          )}
          <Button
            onClick={() => {
              saveData(true);
              handlePurchasesSalesData({
                toYear: balanceFormik.values.to_year,
                toMonth: balanceFormik.values.to_month,
              });
            }}
            id='client-button-save'
            size='lg'
            variant='primary'
            fullWidth
            disabled={!selectedKey || isDisabled || isSaving}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </Card>
  );
};

export default BalanceLoader;
